const EMPTY_STRINGS = new Set(["", "[]", "null", "undefined"]);
const OBJECT_STRING = "[object Object]";
const LEGACY_DOCUMENT_TYPE_FALLBACKS = new Set(["contract"]);
const LEGACY_PARTY_ROLE_ALIASES = {
    BUYER_CLIENT: "CLIENT",
    SUPPLIER: "PROVIDER",
    SERVICE_PROVIDER: "PROVIDER",
    LICENSOR_LICENSEE: "OTHER",
    REGULATOR: "PUBLIC_AUTHORITY",
};

export class MetadataValidationError extends Error {
    constructor(field, value) {
        super(`metadata_invalid:${field}`);
        this.name = "MetadataValidationError";
        this.field = field;
        this.value = value;
    }
}

const cleanString = (value) => {
    const text = String(value ?? "").trim();
    return EMPTY_STRINGS.has(text) ? undefined : text;
};

const assertNotObjectString = (value, field, { strict }) => {
    if (strict && String(value ?? "").trim() === OBJECT_STRING) {
        throw new MetadataValidationError(field, value);
    }
};

const valueFromOptionObject = (value, field, { strict }) => {
    if (!value || typeof value !== "object") return undefined;
    const raw = value.value ?? value.key ?? value.code;
    if (typeof raw === "string" || typeof raw === "number") {
        return cleanString(raw);
    }
    if (strict) {
        throw new MetadataValidationError(field, value);
    }
    return undefined;
};

export const normalizeMetadataSelect = (value, field = "metadata", options = {}) => {
    const { strict = false } = options;
    if (value == null) return undefined;
    if (typeof value === "object") {
        return valueFromOptionObject(value, field, { strict });
    }
    assertNotObjectString(value, field, { strict });
    if (String(value ?? "").trim() === OBJECT_STRING) return undefined;
    const text = cleanString(value);
    if (field === "document_type" && LEGACY_DOCUMENT_TYPE_FALLBACKS.has(String(text ?? "").trim().toLowerCase())) {
        return undefined;
    }
    if (field === "party_role") {
        const normalized = String(text ?? "").trim().toUpperCase();
        return LEGACY_PARTY_ROLE_ALIASES[normalized] || text;
    }
    return text;
};

export const normalizeMetadataList = (value, field = "metadata", options = {}) => {
    const { strict = false } = options;
    if (value == null) return [];

    if (Array.isArray(value)) {
        return value
            .flatMap((item) => normalizeMetadataList(item, field, options))
            .filter(Boolean);
    }

    if (typeof value === "object") {
        const optionValue = valueFromOptionObject(value, field, { strict });
        return optionValue ? [optionValue] : [];
    }

    assertNotObjectString(value, field, { strict });
    if (String(value ?? "").trim() === OBJECT_STRING) return [];
    const text = cleanString(value);
    if (!text) return [];

    if (text.startsWith("[") && text.endsWith("]")) {
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) {
                return normalizeMetadataList(parsed, field, options);
            }
        } catch {
            if (strict) {
                throw new MetadataValidationError(field, value);
            }
        }
    }

    return text
        .split(",")
        .map((item) => cleanString(item))
        .filter(Boolean);
};

export const uniqueMetadataList = (value, field = "metadata", options = {}) => {
    const seen = new Set();
    return normalizeMetadataList(value, field, options).filter((item) => {
        if (seen.has(item)) return false;
        seen.add(item);
        return true;
    });
};

export const serializeMetadataListForApi = (value, field = "metadata") => {
    const values = uniqueMetadataList(value, field, { strict: true });
    return values.length ? values.join(",") : undefined;
};

export const normalizeMetadataDraft = (metadata = {}, options = {}) => {
    const studiedPartyName = normalizeMetadataSelect(
        metadata.studied_party_name,
        "studied_party_name",
        options
    );
    return {
        context: normalizeMetadataSelect(metadata.context, "context", options),
        document_type: normalizeMetadataSelect(metadata.document_type, "document_type", options),
        party_role: normalizeMetadataSelect(
            metadata.party_role,
            studiedPartyName ? "studied_party_role" : "party_role",
            options
        ),
        studied_party_name: studiedPartyName,
        industry: uniqueMetadataList(metadata.industry, "industry", options),
        jurisdiction: uniqueMetadataList(metadata.jurisdiction, "jurisdiction", options),
    };
};

export const isMetadataListPresent = (value, field = "metadata") =>
    uniqueMetadataList(value, field).length > 0;

export const isMetadataComplete = (metadata = {}) => {
    const normalized = normalizeMetadataDraft(metadata);
    return Boolean(
        normalized.context &&
        normalized.document_type &&
        normalized.party_role &&
        normalized.industry.length &&
        normalized.jurisdiction.length
    );
};

export const getMissingMetadataFields = (metadata = {}) => {
    const normalized = normalizeMetadataDraft(metadata);
    const missing = [];
    if (!normalized.context) missing.push("context");
    if (!normalized.document_type) missing.push("document_type");
    if (!normalized.party_role) missing.push("party_role");
    if (!normalized.industry.length) missing.push("industry");
    if (!normalized.jurisdiction.length) missing.push("jurisdiction");
    return missing;
};
